import React, { Component } from 'react';
import { Dialog } from 'primereact/dialog';
import Icon from '../../../icons/Icon';
import FormicaLabel from '../../custom/FormicaLabel';
import FormicaInput from '../../custom/FormicaInput';
import AnimatedButton from '../../custom/AnimatedButton';
import WatchlistService from '../../../service/WatchlistService';
import ServiceContainer from '../../../config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import MessageUtil from '../../../util/MessageUtil';
import LanguageUtil from '../../../util/LanguageUtil';

class WatchlistHistorySettingsComponent extends Component<Props, State> {
  public state = new State();

  private watchlistService: WatchlistService = ServiceContainer.getInstance().get(ServiceNames.WATCHLIST);

  public render() {
    const { visible, onHide } = this.props;
    const { minScore, maxRecords, loading } = this.state;
    return (
      <Dialog
        header={this.getDialogHeader()}
        headerStyle={{ borderBottom: '1px solid #E0E4E8', background: '#F9FAFB', padding: 0, boxShadow: 'none' }}
        contentStyle={{ padding: '0px', height: '100%', display: 'flex' }}
        visible={visible}
        style={{ width: '30vw' }}
        onShow={this.loadConfig}
        onHide={onHide}
      >
        <div className={'flex flex-col w-full h-full gap-y-5 px-5 py-5'}>
          <div className={'flex flex-col gap-y-3 w-full'}>
            <div className={'flex flex-col w-full gap-y-1'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'pages.watchlist.history.settings.minScore', defaultMessage: 'Min Score' })} />
              <FormicaInput
                type={'number'}
                min={0}
                max={100}
                placeholder={'0 - 100'}
                value={minScore}
                disabled={loading}
                onChange={(e: React.FormEvent<HTMLInputElement>) => this.setState({ minScore: e.currentTarget.value })}
              />
            </div>
            <div className={'flex flex-col w-full gap-y-1'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'pages.watchlist.history.settings.maxRecords', defaultMessage: 'Max Records' })} />
              <FormicaInput
                type={'number'}
                min={1}
                max={100}
                placeholder={'1 - 100'}
                value={maxRecords}
                disabled={loading}
                onChange={(e: React.FormEvent<HTMLInputElement>) => this.setState({ maxRecords: e.currentTarget.value })}
              />
            </div>
          </div>
          <div className={'flex w-full items-center justify-end gap-x-5'}>
            <AnimatedButton
              onClick={onHide}
              icon={'multiplier'}
              text={LanguageUtil.get({ id: 'common.label.cancel', defaultMessage: 'Cancel' })}
              customSize={'sm'}
              customType={'secondary'}
            />
            <AnimatedButton
              onClick={this.onSubmit}
              icon={'check'}
              text={LanguageUtil.get({ id: 'common.label.apply', defaultMessage: 'Apply' })}
              customSize={'sm'}
              customType={'primary'}
            />
          </div>
        </div>
      </Dialog>
    );
  }

  private getDialogHeader = () => {
    return (
      <div className={'flex w-full h-[52px] border-b-[1px] border-[#E0E4E8] bg-main-gray items-center px-5 gap-x-3'}>
        <Icon icon={'settings'} className={'text-primary'} size={16} />
        <p className={'text-md text-primary'}>{LanguageUtil.get({ id: 'pages.watchlist.history.settings.title', defaultMessage: 'History Settings' })}</p>
      </div>
    );
  };

  private loadConfig = async () => {
    this.setState({ loading: true });
    try {
      const config = await this.watchlistService.getMatchManyLogConfig();
      this.setState({ minScore: String(config.minScore), maxRecords: String(config.maxRecords), loading: false });
    } catch (e) {
      this.showError(LanguageUtil.get({ id: 'pages.watchlist.history.settings.loadError', defaultMessage: 'Settings could not be loaded!' }));
      this.setState({ loading: false });
    }
  };

  private onSubmit = async () => {
    const { onHide } = this.props;
    const { minScore, maxRecords, loading } = this.state;
    if (loading) {
      return;
    }
    const parsedMinScore = Number(minScore);
    const parsedMaxRecords = Number(maxRecords);
    if (minScore.trim() == '' || !Number.isInteger(parsedMinScore) || parsedMinScore < 0 || parsedMinScore > 100) {
      this.showError(
        LanguageUtil.get({ id: 'pages.watchlist.history.settings.invalidMinScore', defaultMessage: 'Min score must be a whole number between 0 and 100!' }),
      );
      return;
    }
    if (maxRecords.trim() == '' || !Number.isInteger(parsedMaxRecords) || parsedMaxRecords < 1 || parsedMaxRecords > 100) {
      this.showError(
        LanguageUtil.get({
          id: 'pages.watchlist.history.settings.invalidMaxRecords',
          defaultMessage: 'Max records must be a whole number between 1 and 100!',
        }),
      );
      return;
    }
    this.setState({ loading: true });
    try {
      await this.watchlistService.saveMatchManyLogConfig(parsedMinScore, parsedMaxRecords);
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'pages.watchlist.title', defaultMessage: 'Watchlist' }),
        detail: LanguageUtil.get({ id: 'pages.watchlist.history.settings.saveSuccess', defaultMessage: 'Settings saved!' }),
      });
      this.setState({ loading: false });
      onHide();
    } catch (e) {
      this.showError(LanguageUtil.get({ id: 'pages.watchlist.history.settings.saveError', defaultMessage: 'Settings could not be saved!' }));
      this.setState({ loading: false });
    }
  };

  private showError = (detail: string) => {
    MessageUtil.error({
      summary: LanguageUtil.get({ id: 'pages.watchlist.title', defaultMessage: 'Watchlist' }),
      detail,
    });
  };
}

interface Props {
  visible: boolean;
  onHide: () => void;
}

class State {
  minScore: string = '';
  maxRecords: string = '';
  loading: boolean = false;
}

export default WatchlistHistorySettingsComponent;
